import { useEffect, useMemo, useRef, useState } from 'react';
import { QuizFive } from './quiz-five';
import { ATTACHMENT_BY_ID, ATTACHMENT_IDS, CATALOG_CONTEXT, INLINE_BY_ID, INLINE_IDS } from './fixtures';
import {
  FORMAL_STORAGE_KEY, loadDraft, resolveDraftLock, resolveStorage, saveDraft, STORAGE_KEY, withDraftLock,
  type DraftLockManager, type DraftWriteResult, type Snapshot,
} from './model';
import { DEMO_QUIZ, type QuizConfig, type QuizQuestion } from './quiz-config';
import {
  getVisibleQuestions, quizProgress, setQuizAnswer, skipQuizQuestion, validateQuizConfig,
  type QuizAnswers,
} from './quiz-model';
import { buildDemoRecommendation, applyDemoTemplate, type ResolvedDemoTemplate } from './recommendation-model';
import {
  applyFormalTemplate, buildFormalRecommendation, getFormalRecommendationConfig, validateFormalSelection,
  type FormalRecommendationConfig, type ResolvedFormalTemplate,
} from './formal-recommendation';

type QuizScreen = 'intro' | 'questions' | 'complete';
type RecommendationTemplate = ResolvedDemoTemplate | ResolvedFormalTemplate;

function answerLabels(question: QuizQuestion, answers: QuizAnswers): string {
  const selected = new Set(answers[question.id] ?? []);
  const labels = question.options.filter((option) => selected.has(option.code)).map((option) => option.label);
  return labels.length > 0 ? labels.join(', ') : 'Skipped';
}

function TemplatePreview({ template, formal }: { template: RecommendationTemplate; formal: boolean }) {
  return <div className="gbm-quiz__template-preview" aria-label={`${template.name} arrangement preview`}>
    <div className="gbm-quiz__bead-row" aria-hidden="true">
      {template.inlineComponentIds.map((componentId, index) => {
        const fixture = INLINE_BY_ID.get(componentId);
        return <span key={`${componentId}-${index}`} style={{ background: fixture?.color, borderColor: fixture?.accent }} />;
      })}
    </div>
    <p>{template.inlineComponentIds.length} {formal ? 'catalog' : 'synthetic'} pieces{template.attachment ? ` · ${ATTACHMENT_BY_ID.get(template.attachment.componentId)?.name ?? 'Hanging detail'} anchored at piece ${template.attachment.anchorIndex + 1}` : ' · no hanging detail'}</p>
  </div>;
}

export function QuizApp() {
  const formalConfig = getFormalRecommendationConfig();
  const mount = document.getElementById('gbm-quiz-demo');
  const formalLive = mount?.dataset.gbmFormalLive === '1';
  const formalExpected = mount?.dataset.gbmMode === 'formal'
    || CATALOG_CONTEXT.mode === 'formal_preview';
  if (!formalExpected) return <QuizFive />;
  if (formalExpected && !formalConfig) {
    return <section className="gbm-quiz" aria-labelledby="gbm-quiz-title">
      <div className="gbm-quiz__flag">{formalLive ? 'STARTING DESIGNS TEMPORARILY UNAVAILABLE' : 'FORMAL CONFIGURATION UNAVAILABLE · NO TEST FALLBACK'}</div>
      <div className="gbm-quiz__card gbm-quiz__intro">
        <h1 id="gbm-quiz-title">Begin directly in the studio.</h1>
        <p>{formalLive ? 'The guided starting designs are unavailable right now. You can still create your bracelet directly in the studio.' : 'The enabled formal recommendation version is incomplete or no longer matches the active catalog. No synthetic or TEST recommendation was substituted.'}</p>
        <a className="gbm-quiz__primary-link" href={formalLive ? '/design-your-own/' : '/design-your-own/?gbm_catalog=formal'}>{formalLive ? 'Open the design studio' : 'Open the Formal preview studio'}</a>
      </div>
    </section>;
  }
  return <QuizFlow formalConfig={formalConfig} formalLive={formalLive} />;
}

function QuizFlow({ formalConfig, formalLive }: { formalConfig: FormalRecommendationConfig | null; formalLive: boolean }) {
  const formal = formalConfig !== null;
  const quizConfig: QuizConfig = formalConfig?.quiz ?? DEMO_QUIZ;
  const studioUrl = formalConfig?.studioUrl ?? '/design-your-own/';
  const storageKey = formal ? FORMAL_STORAGE_KEY : STORAGE_KEY;
  const lockName = formal ? 'gbm-studio-formal-draft-v1' : 'gbm-studio-draft-v1';
  const [screen, setScreen] = useState<QuizScreen>('intro');
  const [answers, setAnswers] = useState<QuizAnswers>({});
  const [currentId, setCurrentId] = useState(quizConfig.questions[0].id);
  const [trail, setTrail] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<RecommendationTemplate | null>(null);
  const [pendingApply, setPendingApply] = useState<{ snapshot: Snapshot; template: RecommendationTemplate; pieces: number; attachments: number; revision: number } | null>(null);
  const [applyNotice, setApplyNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [writerId] = useState(() => crypto.randomUUID());
  const headingRef = useRef<HTMLHeadingElement>(null);
  const confirmHeadingRef = useRef<HTMLHeadingElement>(null);
  const applyTemplateButtonRef = useRef<HTMLButtonElement>(null);
  const storage = useMemo(() => resolveStorage(() => window.localStorage), []);
  const lockManager = useMemo(() => resolveDraftLock(() => navigator.locks as unknown as DraftLockManager), []);
  const configValid = validateQuizConfig(quizConfig);
  const visibleQuestions = useMemo(() => getVisibleQuestions(quizConfig, answers), [answers, quizConfig]);
  const question = visibleQuestions.find((candidate) => candidate.id === currentId) ?? visibleQuestions[0];
  const progress = question ? quizProgress(quizConfig, answers, question.id) : { step: 1, total: 1, completedPercent: 0 };
  const recommendation = useMemo(
    () => formalConfig ? buildFormalRecommendation(answers, formalConfig) : buildDemoRecommendation(answers),
    [answers, formalConfig],
  );
  const recommendedTemplates: Array<{ label: string; template: RecommendationTemplate }> = recommendation.status !== 'ready'
    ? []
    : 'templates' in recommendation
      ? recommendation.templates.map((template, index) => ({ label: index === 0 ? 'Primary starting point' : 'Alternative starting point', template }))
      : [
        { label: 'Primary starting point', template: recommendation.primary },
        { label: 'Alternative starting point', template: recommendation.alternative },
      ];

  useEffect(() => {
    if (screen !== 'intro') headingRef.current?.focus();
  }, [currentId, screen]);

  useEffect(() => {
    setPreviewTemplate(null);
    setPendingApply(null);
    setApplyNotice(null);
  }, [answers]);

  useEffect(() => {
    if (!pendingApply) return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== STORAGE_KEY) return;
      const latest = loadDraft(storage, INLINE_IDS, ATTACHMENT_IDS, storageKey);
      if (latest.status === 'invalid' || latest.status === 'unavailable' || latest.revision !== pendingApply.revision) {
        setPendingApply(null);
        setApplyNotice({
          kind: 'error',
          text: 'Another window changed the DIY draft. Preview this starting point again before confirming. Nothing was overwritten.',
        });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [pendingApply, storage, storageKey]);

  useEffect(() => {
    if (pendingApply) confirmHeadingRef.current?.focus();
  }, [pendingApply]);

  function start() {
    setAnswers({});
    setTrail([]);
    setCurrentId(quizConfig.questions[0].id);
    setError(null);
    setPreviewTemplate(null);
    setPendingApply(null);
    setApplyNotice(null);
    setScreen('questions');
  }

  async function persistApplied(snapshot: Snapshot, expectedRevision: number, template: RecommendationTemplate) {
    if (formalConfig) {
      const validation = await validateFormalSelection(formalConfig, [(template as ResolvedFormalTemplate).theme]);
      if (!validation.ok) {
        setPendingApply(null);
        setApplyNotice({ kind: 'error', text: validation.message });
        return;
      }
    }
    const locked = await withDraftLock(lockManager, () => saveDraft(
      storage, snapshot, expectedRevision, writerId, INLINE_IDS, ATTACHMENT_IDS, storageKey,
    ), lockName);
    if (!locked.ok) {
      setApplyNotice({ kind: 'error', text: `${locked.message} Nothing was changed.` });
      return;
    }
    const saved: DraftWriteResult = locked.value;
    if (!saved.ok) {
      if (saved.kind === 'conflict') setPendingApply(null);
      setApplyNotice({
        kind: 'error',
        text: saved.kind === 'conflict'
          ? 'Another window changed the DIY draft. Preview this starting point again before confirming. Nothing was overwritten.'
          : `${saved.message} Nothing was changed.`,
      });
      return;
    }
    setPendingApply(null);
    setApplyNotice({ kind: 'success', text: `This ${formal ? 'catalog' : 'synthetic'} starting point is now in your ${formalLive ? 'private browser' : formal ? 'isolated Formal preview' : 'local DIY'} draft. Wrist details were left unchanged; fit is still not validated.` });
  }

  async function requestApply(template: RecommendationTemplate) {
    setApplyNotice(null);
    if (!lockManager) {
      setApplyNotice({ kind: 'error', text: 'Reliable same-origin draft locking is unavailable. The starting point was not saved.' });
      return;
    }
    if (formalConfig) {
      const validation = await validateFormalSelection(formalConfig, [(template as ResolvedFormalTemplate).theme]);
      if (!validation.ok) { setApplyNotice({ kind: 'error', text: validation.message }); return; }
    }
    const draft = loadDraft(storage, INLINE_IDS, ATTACHMENT_IDS, storageKey);
    if (draft.status === 'invalid' || draft.status === 'unavailable') {
      setApplyNotice({ kind: 'error', text: `${draft.message} Nothing was changed.` });
      return;
    }
    const applied = formalConfig
      ? applyFormalTemplate(draft.snapshot, template as ResolvedFormalTemplate, formalConfig)
      : applyDemoTemplate(draft.snapshot, template as ResolvedDemoTemplate);
    if (applied.error) {
      setApplyNotice({ kind: 'error', text: applied.error });
      return;
    }
    const pieces = draft.snapshot.design.inline_items.length;
    const attachments = draft.snapshot.design.attachments.length;
    if (draft.status === 'loaded' && (pieces > 0 || attachments > 0)) {
      setPendingApply({ snapshot: applied.snapshot, template, pieces, attachments, revision: draft.revision });
      return;
    }
    void persistApplied(applied.snapshot, draft.revision, template);
  }

  function cancelPendingApply() {
    setPendingApply(null);
    requestAnimationFrame(() => applyTemplateButtonRef.current?.focus());
  }

  function choose(code: string) {
    if (!question) return;
    const current = answers[question.id] ?? [];
    let codes: string[];
    if (question.kind === 'multi') {
      codes = current.includes(code) ? current.filter((item) => item !== code) : [...current, code];
      if (codes.length === 0) {
        const skipped = skipQuizQuestion(quizConfig, answers, question.id);
        setAnswers(skipped.answers);
        setError(skipped.error ?? null);
        return;
      }
    } else {
      codes = [code];
    }
    const result = setQuizAnswer(quizConfig, answers, question.id, codes);
    setAnswers(result.answers);
    setError(result.error ?? null);
  }

  function advance(nextAnswers = answers) {
    if (!question) return;
    const route = getVisibleQuestions(quizConfig, nextAnswers);
    const index = route.findIndex((candidate) => candidate.id === question.id);
    const nextQuestion = route[index + 1];
    setTrail((current) => [...current, question.id]);
    setError(null);
    if (nextQuestion) setCurrentId(nextQuestion.id);
    else setScreen('complete');
  }

  function continueForward() {
    if (!question) return;
    if (!(answers[question.id]?.length > 0)) {
      setError(question.required ? 'Choose one option to continue.' : 'Choose an option, or use “Skip this question”.');
      return;
    }
    advance();
  }

  function skip() {
    if (!question) return;
    const result = skipQuizQuestion(quizConfig, answers, question.id);
    if (result.error) { setError(result.error); return; }
    setAnswers(result.answers);
    advance(result.answers);
  }

  function back() {
    const previous = trail.at(-1);
    if (!previous) { setScreen('intro'); setError(null); return; }
    setTrail((current) => current.slice(0, -1));
    setCurrentId(previous);
    setScreen('questions');
    setError(null);
  }

  if (!configValid) {
    return <section className="gbm-quiz"><p role="alert">The question configuration is unavailable. Continue directly to DIY.</p></section>;
  }

  return (
    <section className="gbm-quiz" aria-labelledby="gbm-quiz-title" data-review-status={quizConfig.reviewStatus}>
      <div className="gbm-quiz__flag">{formalLive ? 'A GENTLE PLACE TO BEGIN' : formal ? 'SYNTHETIC FORMAL PREVIEW · NOT APPROVED FOR LIVE USE' : 'DEMO · STAGING · QUESTIONS NOT YET APPROVED'}</div>
      <header className="gbm-quiz__header">
        <div>
          <p className="gbm-quiz__eyebrow">A gentle starting point</p>
          <h1 id="gbm-quiz-title">Find a direction, then make it yours.</h1>
          <p>{formalLive ? 'This short flow uses the current catalog to offer editable starting arrangements.' : formal ? 'This short flow uses the active Formal preview catalog to offer editable starting arrangements.' : 'This short flow offers synthetic starting arrangements from visual choices only. They are not product recommendations.'}</p>
        </div>
        <a className="gbm-quiz__direct-link" href={studioUrl}>Go directly to DIY <span aria-hidden="true">→</span></a>
      </header>

      {screen === 'intro' && <div className="gbm-quiz__card gbm-quiz__intro">
        <div className="gbm-quiz__crest" aria-hidden="true">◇</div>
        <p className="gbm-quiz__eyebrow">{formalLive ? 'Your preferences' : formal ? 'Formal preview' : 'Visual demo'}</p>
        <h2>Explore without being assessed.</h2>
        <p>Choose what catches your eye, skip optional questions, or leave for the studio at any time. There are no scores, diagnoses, or hidden personality claims.</p>
        <div className="gbm-quiz__privacy-note">
          <strong>Only in this page's memory</strong>
          <span>Your answers are not saved or sent. Refreshing or leaving starts this question flow again, while your separate DIY draft stays untouched.</span>
        </div>
        <div className="gbm-quiz__intro-actions">
          <button type="button" onClick={start}>Begin {formal ? 'the questions' : 'the demo'}</button>
          <a href={studioUrl}>Skip all questions</a>
        </div>
      </div>}

      {screen === 'questions' && question && <div className="gbm-quiz__card gbm-quiz__question-card">
        <div className="gbm-quiz__progress-copy">
          <span>Question {progress.step} of {progress.total}</span>
          <span>{question.required ? 'Required' : 'Optional'}</span>
        </div>
        <div className="gbm-quiz__progress" role="progressbar" aria-label="Question progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.completedPercent}>
          <span style={{ width: `${progress.completedPercent}%` }} />
        </div>
        <div className="gbm-quiz__question-heading">
          <h2 ref={headingRef} tabIndex={-1}>{question.prompt}</h2>
          <span>{question.help}</span>
        </div>
        <div className={`gbm-quiz__options is-${question.kind}`} role="group" aria-label={question.prompt}>
          {question.options.map((option) => {
            const selected = answers[question.id]?.includes(option.code) ?? false;
            return <button type="button" key={option.code} className={selected ? 'is-selected' : ''} aria-pressed={selected} onClick={() => choose(option.code)}>
              {option.swatch && <span className="gbm-quiz__swatch" style={{ background: option.swatch }} aria-hidden="true" />}
              <span><strong>{option.label}</strong>{option.note && <small>{option.note}</small>}</span>
              <span className="gbm-quiz__check" aria-hidden="true">{selected ? '✓' : '○'}</span>
            </button>;
          })}
        </div>
        {question.kind === 'multi' && <p className="gbm-quiz__selection-count">{answers[question.id]?.length ?? 0} of {question.maxSelections} selected</p>}
        {error && <p className="gbm-quiz__error" role="alert">{error}</p>}
        <div className="gbm-quiz__question-actions">
          <button type="button" className="is-quiet" onClick={back}>Back</button>
          {!question.required && <button type="button" className="is-quiet" onClick={skip}>Skip this question</button>}
          <button type="button" onClick={continueForward}>Continue</button>
        </div>
      </div>}

      {screen === 'complete' && <div className="gbm-quiz__card gbm-quiz__complete">
        <p className="gbm-quiz__eyebrow">{formalLive ? 'Starting designs' : formal ? 'Formal preview starting designs' : 'Synthetic demo starting points'}</p>
        <h2 ref={headingRef} tabIndex={-1}>A visual place to begin—not an assessment.</h2>
        <p>{formalLive ? 'These editable arrangements reference the current catalog and are rechecked before applying. They do not decide meaning, wrist fit, or suitability for you.' : formal ? 'These editable arrangements reference the current Formal preview catalog and are rechecked before applying. They do not confirm price, wrist fit, meaning, or suitability.' : 'These arrangements use demo fixtures only. They do not identify real materials or confirm price, inventory, wrist fit, meaning, or suitability.'}</p>
        <dl className="gbm-quiz__summary">
          {getVisibleQuestions(quizConfig, answers).map((item) => <div key={item.id}>
            <dt>{item.prompt}</dt><dd>{answerLabels(item, answers)}</dd>
          </div>)}
        </dl>
        {recommendation.status === 'ready' ? <>
          <div className="gbm-quiz__why">
            <strong>What shaped these starting points</strong>
            <ul>{recommendation.explanations.map((explanation) => <li key={explanation}>{explanation}</li>)}</ul>
          </div>
          <div className="gbm-quiz__recommendations">
            {recommendedTemplates.map(({ label, template: item }) => {
              return <article key={item.id}>
                <p className="gbm-quiz__eyebrow">{label}</p>
                <h3>{item.name}</h3>
                <p>{item.note}</p>
                <TemplatePreview template={item} formal={formal} />
                <button type="button" className="is-quiet" onClick={() => { setPreviewTemplate(item); setPendingApply(null); setApplyNotice(null); }}>Preview this starting point</button>
              </article>;
            })}
          </div>
        </> : <div className="gbm-quiz__unavailable" role="status">
          <strong>No {formal ? 'formal' : 'demo'} starting point is available.</strong>
          {recommendation.unavailableReasons.map((reason) => <p key={reason}>{reason}</p>)}
          <p>Your answers were not replaced with a guess.</p>
        </div>}
        {previewTemplate && <section className="gbm-quiz__selected-preview" aria-labelledby="gbm-selected-preview-title">
          <p className="gbm-quiz__eyebrow">Preview before applying</p>
          <h3 id="gbm-selected-preview-title">{previewTemplate.name}</h3>
          <p>{previewTemplate.note}</p>
          <TemplatePreview template={previewTemplate} formal={formal} />
          <p className="gbm-quiz__preview-warning">Applying replaces only the DIY arrangement and hanging details. It preserves wrist measurement and wear preference, but does not validate fit.</p>
          <div className="gbm-quiz__preview-actions">
            <button type="button" className="is-quiet" onClick={() => { setPreviewTemplate(null); setPendingApply(null); setApplyNotice(null); }}>Cancel preview</button>
            <button type="button" ref={applyTemplateButtonRef} onClick={() => void requestApply(previewTemplate)}>Use this starting point in DIY</button>
          </div>
        </section>}
        {pendingApply && <section className="gbm-quiz__confirm" aria-labelledby="gbm-replace-title">
          <h3 id="gbm-replace-title" ref={confirmHeadingRef} tabIndex={-1}>Replace your current DIY arrangement?</h3>
          <p>Your draft has {pendingApply.pieces} piece{pendingApply.pieces === 1 ? '' : 's'} and {pendingApply.attachments} hanging detail{pendingApply.attachments === 1 ? '' : 's'}. Confirming replaces those only. Your wrist measurement and wear preference remain, and fit is not validated.</p>
          <div>
            <button type="button" className="is-quiet" onClick={cancelPendingApply}>Keep my current draft</button>
            <button type="button" onClick={() => void persistApplied(pendingApply.snapshot, pendingApply.revision, pendingApply.template)}>Replace arrangement</button>
          </div>
        </section>}
        {applyNotice && <div className={`gbm-quiz__apply-notice is-${applyNotice.kind}`} role={applyNotice.kind === 'error' ? 'alert' : 'status'}>
          <p>{applyNotice.text}</p>
          {applyNotice.kind === 'success' && <a className="gbm-quiz-open-applied" href={studioUrl}>Open the applied draft <span aria-hidden="true">→</span></a>}
        </div>}
        <div className="gbm-quiz__privacy-note"><strong>Answers stay in this page flow</strong><span>This flow does not store or send your answers or explanations. Only a starting arrangement you explicitly apply may be saved under the existing {formalLive ? 'private browser' : formal ? 'isolated Formal preview' : 'local DIY'} draft rules.</span></div>
        <div className="gbm-quiz__complete-actions">
          <button type="button" className="is-quiet" onClick={back}>Review my answers</button>
          <a className="gbm-quiz__primary-link" href={studioUrl}>Continue my existing design <span aria-hidden="true">→</span></a>
        </div>
      </div>}

      <footer className="gbm-quiz__footer">{formalLive ? 'No assessment, outcome promise, fit decision, account profile, or email' : formal ? 'Formal preview only · no assessment, outcome promise, fit decision, account profile, or email' : 'Synthetic demo only · no material recommendation, price, inventory, fit decision, account, or email'}</footer>
    </section>
  );
}
